import { RootState } from '../store/configureStore';
import { PaymentEligibilityTypes } from '../constants';
/* My Cars Selectors */

export const getMyCars = (state: RootState) => state.myCars.cars;

// Get id of currently selected vehicle
export const getCurrentVehicleId = (state: RootState) => {
  const {
    myCars: {
      currentVehicle: { vehicleId },
    },
  } = state;
  return vehicleId;
};

// Get Current Vehicle Object

export const getCurrentVehicle = (state: RootState) => {
  const {
    myCars: { cars },
  } = state;
  const vehicleId = getCurrentVehicleId(state);
  return cars.filter((obj: any) => obj.vehicle.vehicleId === vehicleId)[0];
};

// Get the list of one-time payment schedules
export const getMyOnetimePaymentSchedules = (state: RootState) => {
  const currentCarObject = getCurrentVehicle(state);
  if (currentCarObject) {
    const {
      loanDetails: { paymentWithdrawSchedules },
    } = currentCarObject;
    if (paymentWithdrawSchedules) {
      return paymentWithdrawSchedules
        .filter(
          (paymentWithdrawSchedule: any) => !paymentWithdrawSchedule.isRecurring
        )
        .sort(sortPaymentWithdrawSchedules);
    }
  }
  return [];
};

// Get the list of all payment schedules
export const getAllPaymentSchedules = (state: RootState) => {
  const currentCarObject = getCurrentVehicle(state);
  if (currentCarObject) {
    const {
      loanDetails: { paymentWithdrawSchedules },
    } = currentCarObject;
    if (paymentWithdrawSchedules) {
      return paymentWithdrawSchedules.sort(sortPaymentWithdrawSchedules);
    }
  }
  return [];
};

export const getBankInfo = (state: RootState) => {
  const currentCarObject = getCurrentVehicle(state);
  if (currentCarObject) {
    const { bankInfo } = currentCarObject;
    return bankInfo;
  }
  return [];
};
// TODO: Needs work
// Get auto-pay schedule
export const getAutoPaySchedule = (state: RootState) => {
  const currentCarObject = getCurrentVehicle(state);
  if (currentCarObject) {
    const {
      loanDetails: { paymentWithdrawSchedules },
    } = currentCarObject;
    return paymentWithdrawSchedules
      .filter(
        (paymentWithdrawSchedule: any) => paymentWithdrawSchedule.isRecurring
      )
      .sort(sortPaymentWithdrawSchedules);
  }
  return [];
};

export const getPaymentEligiblity = (state: RootState) => {
  const currentCarObject = getCurrentVehicle(state);
  if (currentCarObject) {
    const {
      loanDetails: { paymentEligibility },
    } = currentCarObject;
    return paymentEligibility;
  }
  return {};
};

// Get the list of one-time payment schedules
export const getLoanDetails = (state: RootState) => {
  const currentCarObject = getCurrentVehicle(state);
  if (currentCarObject) {
    const {
      loanDetails: { cvnaPaymentAmountDue, principalDueDate },
    } = currentCarObject;
    return {
      cvnaPaymentAmountDue,
      principalDueDate,
    };
  }
  return {};
};

/** Selector to display One-time payment required modal on click of Setup Auto Pay */
export const getPaymentEligibilityByType = (
  state: RootState,
  eligibilityType: string
) => {
  const currentCarObject = getCurrentVehicle(state);
  if (currentCarObject) {
    const {
      loanDetails: {
        paymentEligibility: {
          recurringPaymentEligibility: {
            recurringPaymentEligibilityStatus,
            totalAmountDueTodayWithPending,
          },
          oneTimePaymentEligibility: { isEligible: isOneTimePaymentEligible },
        },
      },
    } = currentCarObject;

    switch (eligibilityType) {
      // Pay Current Due before setting up auto-pay
      case PaymentEligibilityTypes.PAY_CURRENT_DUE:
        /** One-time pay eligible, recurringPaymentEligibilityStatus is CONDITIONAL
         * and totalAmountDueTodayWithPending > 0 and is eligible for one-time payment
         * */
        if (
          recurringPaymentEligibilityStatus === 'CONDITIONAL' &&
          totalAmountDueTodayWithPending > 0 &&
          isOneTimePaymentEligible
        ) {
          return true;
        }
        break;

      // In-eligible for one-time payment
      case PaymentEligibilityTypes.ONE_TIME_PAYMENT:
        /** One-time pay in-eligible */
        if (!isOneTimePaymentEligible) {
          return true;
        }
        break;

      // In-eligible for auto-payment
      case PaymentEligibilityTypes.AUTO_PAYMENT:
        /** One-time pay eligible, recurringPaymentEligibilityStatus is CONDITIONAL and
         *  totalAmountDueTodayWithPending === 0 or recurringPaymentEligibilityStatus is INELIGIBLE
         * */
        if (
          isOneTimePaymentEligible &&
          ((recurringPaymentEligibilityStatus === 'CONDITIONAL' &&
            totalAmountDueTodayWithPending === 0) ||
            recurringPaymentEligibilityStatus === 'INELIGIBLE')
        ) {
          return true;
        }
        break;

      // In-eligible for both one-time and auto payments
      case PaymentEligibilityTypes.ALL_PAYMENTS:
        /** One-time pay in-eligible and recurringPaymentEligibilityStatus is CONDITIONAL and totalAmountDueTodayWithPending === 0 or auto-pay in-eligible and
         * */
        if (
          !isOneTimePaymentEligible &&
          ((recurringPaymentEligibilityStatus === 'CONDITIONAL' &&
            totalAmountDueTodayWithPending === 0) ||
            recurringPaymentEligibilityStatus === 'INELIGIBLE')
        ) {
          return true;
        }
        break;

      default:
        break;
    }
  }
  return false;
};

export const getContactDetails = (state: RootState) => {
  const {
    myCars: { contactDetails },
  } = state;
  return contactDetails || undefined;
};

export const getLegalName = (state: RootState) => {
  const contactDetails = getContactDetails(state);
  if (contactDetails?.content?.contact?.name) {
    const { first, last } = contactDetails.content.contact.name;
    return `${first} ${last}`;
  }
  return '';
};

export const getAutopayDetails = (state: RootState) => {
  const currentCarObj = getCurrentVehicle(state);
  if (currentCarObj) {
    const { autopayDetailsRequest } = currentCarObj;
    return autopayDetailsRequest;
  }
  return {};
};

export const getTransactionHistory = (state: RootState) => {
  const currentCarObj = getCurrentVehicle(state);
  if (currentCarObj) {
    const { transactionHistory } = currentCarObj;
    return transactionHistory;
  }
  return {};
};

const sortPaymentWithdrawSchedules = (
  paymentWithdrawSchedule1: any,
  paymentWithdrawSchedule2: any
) => {
  const date1 = new Date(paymentWithdrawSchedule1.paymentDate);
  const date2 = new Date(paymentWithdrawSchedule2.paymentDate);
  return date1.getTime() - date2.getTime();
};

export const getAutopayDetailsPromptData = (state: RootState) => {
  const currentCarObj = getCurrentVehicle(state);
  if (currentCarObj) {
    const { autopayDetailsPromptData } = currentCarObj;
    return autopayDetailsPromptData;
  }
  return {};
};

export const getLoanDetailsInProgress = (state: RootState) =>
  state.myCars.isLoanDetailsInprogress;

export const getOneTimePaymentDetails = (state: RootState) => {
  const currentCarObj = getCurrentVehicle(state);
  if (currentCarObj) {
    const { oneTimePaymentDetails } = currentCarObj;
    return oneTimePaymentDetails;
  }
  return {};
};
